import { useState } from 'react';

import {
  Avatar,
  Divider,
  IconChevronDownOutline,
  IconChevronUpOutline,
  IconExternal,
  IconUser,
  Image,
  Menu,
  Spacer,
} from '@zillow/constellation';

import { shouldDisplayResources } from '../../../../utils';

import HeaderNavigationClickstreamEvents from '../../../../models/HeaderNavigationClickstreamEvents';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../../../constants';

import { ZRMUtils } from '../../../../utils';

import NavigationItem from '../NavigationItem';

import { StyledChevronIconContainer, StyledContainer, StyledDisplayNameOrEmailText, StyledMenuPopper } from './style';

import type { Paths } from '@zg-rentals/paths-manifest';
import type { Displays } from '../../../../interfaces/Displays';
import type { User } from '../../../../interfaces/user';

type Props = {
  displays: Partial<Displays>;
  paths: Partial<Paths>;

  userModel?: User;
};

const AccountMenu = ({
  displays,
  paths,
  userModel = {
    features: {},
  } as User,
}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const {
    displayBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainBilling,
    displayMainHelp,
    displayMainResources,
    displayUserManagement,
  } = displays;

  const {
    billingDue,
    billingPage,
    helpPage = '',
    homePage,
    mfhResourcesPage,
    postLogout = '',
    resourcesPage,
    userManagementPage,
    userSettingsPage = '',
  } = paths;

  const { email = '', firstName = '', lastName = '', loggedIn, profileImageUrl = '' } = userModel;
  const fullName = `${firstName} ${lastName}`.trim();
  const isFirstOrLastNameEmptyOrNull = ZRMUtils.isEmptyOrNull(firstName) || ZRMUtils.isEmptyOrNull(lastName);
  const displayName = isFirstOrLastNameEmptyOrNull === false ? fullName : email;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  const menu = (
    <Menu>
      <Spacer marginBottom="xs" />

      {displayMainAccountSettings && (
        <NavigationItem
          title={HeaderTitles.accountSettings}
          path={userSettingsPage}
          target="_blank"
          TrailingIcon={<IconExternal />}
          marginBottom={0}
          onClick={() => handleLinkClick(HeaderTitles.accountSettings)}
        />
      )}

      {displayBilling && !displayMainBilling && (
        <NavigationItem
          title={HeaderTitles.billing}
          path={`${billingPage}/${billingDue}`}
          marginBottom={0}
          onClick={() => handleLinkClick(HeaderTitles.billing)}
        />
      )}

      {displayUserManagement && (
        <NavigationItem
          title={HeaderTitles.userManagement}
          path={userManagementPage}
          marginBottom={0}
          onClick={() => handleLinkClick(HeaderTitles.userManagement)}
        />
      )}

      <Divider marginY="xs" />

      {shouldDisplayResources(displayMFHResources, displayMainResources) && (
        <>
          {displayMFHResources ? (
            <NavigationItem
              title={HeaderTitles.mfhResources}
              path={mfhResourcesPage}
              marginBottom={0}
              target="_blank"
              TrailingIcon={<IconExternal />}
              onClick={() => handleLinkClick(HeaderTitles.mfhResources)}
            />
          ) : (
            <NavigationItem
              // marginBottom="md"
              title={HeaderTitles.resources}
              path={resourcesPage}
              marginBottom={0}
              target="_blank"
              TrailingIcon={<IconExternal />}
              onClick={() => handleLinkClick(HeaderTitles.resources)}
            />
          )}
        </>
      )}

      {displayMainHelp && (
        <NavigationItem
          title={HeaderTitles.help}
          path={helpPage}
          marginBottom={0}
          target="_blank"
          TrailingIcon={<IconExternal />}
          onClick={() => handleLinkClick(HeaderTitles.help)}
        />
      )}

      <Divider marginY="xs" />

      <NavigationItem
        title={HeaderTitles.backToBrand}
        path={homePage}
        marginBottom={0}
        target="_blank"
        TrailingIcon={<IconExternal />}
        onClick={() => handleLinkClick(HeaderTitles.backToBrand)}
      />

      <NavigationItem
        title={HeaderTitles.signOut}
        path={postLogout}
        marginBottom={0}
        escape={true}
        onClick={() => handleLinkClick(HeaderTitles.signOut)}
      />

      <Spacer marginTop="xs" />
    </Menu>
  );

  return (
    <StyledMenuPopper
      data-testid="AccountMenu"
      triggered={menu}
      onOpen={() => {
        setIsMenuOpen(true);
      }}
      onClose={() => {
        setIsMenuOpen(false);
      }}
    >
      <StyledContainer data-testid="AccountMenuContainer">
        {profileImageUrl ? (
          <Avatar size="sm" marginRight="xs">
            <Image alt={displayName} src={profileImageUrl} className="fs-exclude" />
          </Avatar>
        ) : (
          <>
            {isFirstOrLastNameEmptyOrNull === false ? (
              <Avatar fullName={fullName} size="sm" marginRight="xs" background="aqua500" className="fs-exclude" />
            ) : (
              <Avatar size="sm" marginRight="xs" background="aqua500">
                <IconUser />
              </Avatar>
            )}
          </>
        )}

        <StyledDisplayNameOrEmailText className="fs-mask">{displayName}</StyledDisplayNameOrEmailText>

        <Spacer marginRight={0.5} />

        <StyledChevronIconContainer offsetTop={isMenuOpen ? 1 : 2}>
          {isMenuOpen ? <IconChevronUpOutline size="xs" /> : <IconChevronDownOutline size="xs" />}
        </StyledChevronIconContainer>
      </StyledContainer>
    </StyledMenuPopper>
  );
};

export default AccountMenu;
